import { normalizeName } from '../shared/naming';
import type { ProjectId } from '../shared/ids';
import { measureUnit, serviceElement, type AppliesTo } from './applies-to';
import type { MeasureBy } from './geometry';
import type { IndexOrigin } from './index-origin';
import { serviceLockKey, serviceLockKeyOf } from './project-budget';
import { referenceItemFor, type RateAverageItem, type ReferenceItem } from './rate-average';
import type { RoomKind } from './room';
import { descendsToRooms } from './room-quantity';
import { STEEL_GAUGES, steelRowsFor, type SteelGauge } from './steel';

/**
 * A média do apartamento: quanto de cada item as obras marcadas usam por unidade da medida da
 * planta, nos serviços de parede, de ambiente, de vão e nos que não têm desenho (Fase 40, etapa 1
 * — A1, A5; RN-517 a RN-519).
 *
 * ```text
 * REVESTIMENTO INTERNO · CHAPISCO · ARGAMASSA DE CHAPISCO    0,0050 M³/m² · 1 obra, 1 tipologia
 * INSTALAÇÕES · INFRAESTRURURA ELETRICA · ELETRODUTO 25 MM   15,0000 M/un
 * ```
 *
 * A estaca tem a dela, por diâmetro, no tipo (`rate-average.ts`, Fase 39). Aqui não há peça da
 * planilha para medir: o número próprio de cada obra é o índice dela vezes a medida que a tela
 * fotografou de cada tipologia (ADR-182), ou a quantidade que a tipologia digitou por cima. A
 * divisão de novo pela medida devolve o índice com a perda dentro (D13) — com uma obra só, a
 * média é o índice dela; com várias, a média ponderada pela medida de cada uma (D7).
 *
 * > **As regras em uma linha:** só o serviço do apartamento entra (`isApartmentService`); o item
 * > que segue a média fica fora (D5); o item só entra se casa com a referência (D3); cada
 * > tipologia pesa pelas vezes que se repete na obra.
 *
 * Função pura sobre o que o repositório leu, como a da estaca (ADR-179).
 */

// ── Que serviço é do apartamento ────────────────────────────────────────────────

/**
 * Os elementos do apartamento: a parede e as faces dela, o ambiente (piso, teto, rodapé), os vãos
 * e o serviço sem desenho (`NENHUM`, o "por unidade" — A5), e desde a etapa 3 a laje (RN-531): o
 * concreto pelo volume, a fôrma pelo contorno, o escoramento pela área e o controle pela contagem.
 * O aço da laje vai pela taxa da disciplina (`averageSlabSteelRates`). A estaca segue pelo tipo
 * (Fase 39); pilar, viga e escada ficam fora.
 */
export const APARTMENT_ELEMENTS: ReadonlySet<AppliesTo> = new Set<AppliesTo>([
  'PAREDE',
  'AMBIENTE',
  'PORTA',
  'JANELA',
  'PASSAGEM',
  'GUARDA_CORPO',
  'NENHUM',
  'LAJE',
]);

export function isApartmentService(service: {
  readonly measureBy: MeasureBy;
  readonly appliesTo?: AppliesTo;
}): boolean {
  const elemento = serviceElement(service);
  return elemento !== undefined && APARTMENT_ELEMENTS.has(elemento);
}

/**
 * A unidade que divide o item: a da medida da planta ("m²", "m"), ou a do serviço no que não
 * tem desenho — "un", quando o serviço não diz.
 */
export function apartmentMeasureUnit(service: {
  readonly measureBy: MeasureBy;
  readonly appliesTo?: AppliesTo;
  readonly unit?: string;
}): string {
  const daPlanta = measureUnit(serviceElement(service), service.measureBy);
  if (daPlanta !== '') return daPlanta;
  return service.unit?.trim() === undefined || service.unit.trim() === ''
    ? 'un'
    : service.unit.trim();
}

// ── O que o repositório lê ──────────────────────────────────────────────────────

/**
 * A foto da medida de um serviço numa tipologia (RN-516): o medido que o índice multiplica e as
 * vezes que a tipologia se repete na obra.
 */
export interface ApartmentMeasure {
  readonly projectId: ProjectId;
  readonly unitTypeId: string;
  /** A chave do catálogo (`serviceKey`), como a tela a tem. */
  readonly serviceKey: string;
  readonly measured: number;
  readonly times: number;
}

/** Um item vivo de um serviço do apartamento numa obra marcada. */
export interface ApartmentItem extends RateAverageItem {
  readonly id: string;
  readonly projectId: ProjectId;
  /** A chave do catálogo do serviço do item. */
  readonly serviceKey: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly measureBy: MeasureBy;
  readonly appliesTo?: AppliesTo;
  /** A unidade do serviço — a do "por unidade". */
  readonly serviceUnit?: string;
  readonly consumptionIndex?: number;
  readonly lossPercent?: number;
  readonly indexOrigin?: IndexOrigin;
}

/** A quantidade que uma tipologia digitou para um item, por cima do calculado (a discordância). */
export interface ApartmentOverride {
  readonly unitTypeId: string;
  readonly itemId: string;
  readonly quantity: number;
}

/** A foto da medida de um serviço de ambiente num cômodo (Fase 40, etapa 2 — RN-525). */
export interface ApartmentRoomMeasure {
  readonly projectId: ProjectId;
  readonly unitTypeId: string;
  readonly roomId: string;
  readonly roomKind: RoomKind;
  readonly serviceKey: string;
  readonly measured: number;
  readonly times: number;
}

/** O fator próprio de um cômodo de uma obra marcada, com a origem (RN-527). */
export interface ApartmentRoomFactor {
  readonly roomId: string;
  readonly serviceKey: string;
  /** A chave do item no catálogo da obra: a descrição normalizada. */
  readonly itemKey: string;
  readonly factor: number;
  readonly origin?: IndexOrigin;
}

export interface ApartmentInput {
  readonly measures: readonly ApartmentMeasure[];
  readonly items: readonly ApartmentItem[];
  readonly overrides: readonly ApartmentOverride[];
  /** A medida de cada cômodo nos serviços de ambiente (etapa 2). Ausente: nenhuma. */
  readonly roomMeasures?: readonly ApartmentRoomMeasure[];
  readonly roomFactors?: readonly ApartmentRoomFactor[];
  /** A taxa de aço das disciplinas das obras marcadas (etapa 3). Ausente: nenhuma. */
  readonly steelRates?: readonly ApartmentSteelRate[];
  /** O volume de concreto que a taxa de cada disciplina multiplica, por tipologia (etapa 3). */
  readonly steelVolumes?: readonly ApartmentSteelVolume[];
}

/** A taxa de uma bitola numa disciplina de uma obra marcada, com a origem (RN-532). */
export interface ApartmentSteelRate {
  readonly projectId: ProjectId;
  /** O nome da disciplina normalizado — a primeira metade do `serviceKey`. */
  readonly disciplineKey: string;
  readonly gauge: SteelGauge;
  readonly kgPerM3: number;
  readonly origin?: IndexOrigin;
  /**
   * O elemento da lista, na disciplina que mistura elementos (Fase 42, RN-573). Ausente: a
   * disciplina inteira. A conta lê a do elemento da peça (`steelRowsFor`).
   */
  readonly element?: AppliesTo;
}

/**
 * A foto do volume da laje (etapa 3): por tipologia × disciplina que compra o aço da laje, os m³
 * de concreto que a taxa multiplica, e as vezes que a tipologia se repete.
 */
export interface ApartmentSteelVolume {
  readonly projectId: ProjectId;
  readonly unitTypeId: string;
  readonly disciplineKey: string;
  readonly volume: number;
  readonly times: number;
}

// ── O que a conta devolve ───────────────────────────────────────────────────────

export interface ApartmentAverage {
  /** A chave da trava do serviço (`serviceLockKey`): a mesma em todas as obras. */
  readonly serviceKey: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  /** O item como a referência o escreve. */
  readonly item: ReferenceItem;
  /** "m²", "m", "un"… — a unidade da medida que divide o item. */
  readonly measureUnit: string;
  /** Σ (quantidade × vezes) ÷ Σ (medido × vezes) (RN-518). */
  readonly rate: number;
  readonly quantity: number;
  readonly measure: number;
  /** De quantas obras e de quantas tipologias veio. */
  readonly projects: number;
  readonly unitTypes: number;
}

export type ApartmentOutReason =
  /** O item não casa com nenhum item da referência no mesmo serviço. */
  'ITEM_SEM_PAR';

export const APARTMENT_OUT_LABEL: Readonly<Record<ApartmentOutReason, string>> = {
  ITEM_SEM_PAR: 'item que a obra de referência não tem',
};

/** O que ficou fora, uma vez por obra × serviço × item, com quantas tipologias ele tinha. */
export interface ApartmentOutOfAverage {
  readonly reason: ApartmentOutReason;
  readonly projectId: ProjectId;
  readonly serviceName: string;
  readonly description: string;
  readonly unitTypes: number;
}

/**
 * A média de um item num tipo de cômodo (Fase 40, etapa 2 — A2, RN-525): o banheiro tem a dele,
 * o quarto a dele. Só nos serviços que medem o ambiente, e nunca no "Outro" (A7).
 */
export interface ApartmentRoomKindAverage {
  readonly serviceKey: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly item: ReferenceItem;
  readonly roomKind: RoomKind;
  readonly measureUnit: string;
  readonly rate: number;
  readonly quantity: number;
  readonly measure: number;
  readonly projects: number;
  readonly rooms: number;
}

/** A média da taxa de uma bitola no aço da laje (etapa 3 — RN-532). */
export interface ApartmentSteelAverage {
  readonly gauge: SteelGauge;
  /** Σ (taxa × volume) ÷ Σ volume, em kg/m³. */
  readonly rate: number;
  /** Σ volume das obras que entraram, em m³. */
  readonly volume: number;
  readonly projects: number;
}

export interface ApartmentAverages {
  readonly averages: readonly ApartmentAverage[];
  readonly outOfAverage: readonly ApartmentOutOfAverage[];
  /** A média por tipo de cômodo (etapa 2). */
  readonly byRoomKind: readonly ApartmentRoomKindAverage[];
  /** A média da taxa de aço da laje, por bitola (etapa 3). */
  readonly steel: readonly ApartmentSteelAverage[];
  /** O volume de laje desenhado nas obras marcadas, em m³ — zero é "sem laje" (RN-535). */
  readonly slabVolume: number;
}

export const NO_APARTMENT_AVERAGES: ApartmentAverages = {
  averages: [],
  outOfAverage: [],
  byRoomKind: [],
  steel: [],
  slabVolume: 0,
};

// ── O número próprio ────────────────────────────────────────────────────────────

/**
 * O número próprio de um item numa tipologia (RN-517): a quantidade digitada, quando há; senão,
 * índice × medido × (1 + perda). Sem índice e sem digitado, a tipologia não tem número para o
 * item. O item cujo índice veio da média não tem número próprio (D5) — quem chama o tira antes.
 */
export function ownQuantity(
  item: Pick<ApartmentItem, 'consumptionIndex' | 'lossPercent' | 'kind'>,
  measured: number,
  override: number | undefined,
): number | undefined {
  if (override !== undefined) return override > 0 ? override : undefined;
  const indice = item.consumptionIndex;
  if (indice === undefined || !(indice > 0) || !(measured > 0)) return undefined;
  const perda = item.kind === 'MAO_DE_OBRA' ? 0 : (item.lossPercent ?? 0);
  return indice * measured * (1 + perda / 100);
}

// ── A conta ─────────────────────────────────────────────────────────────────────

interface Acumulado {
  readonly serviceKey: string;
  readonly item: ReferenceItem;
  readonly unit: string;
  readonly ordem: number;
  quantity: number;
  measure: number;
  readonly projects: Set<ProjectId>;
  readonly unitTypes: Set<string>;
}

/**
 * A identidade do item da referência dentro do serviço — a linha da média. A tela a usa para
 * achar a média de um item que ela já casou com a referência.
 */
export function apartmentReferenceKey(item: ReferenceItem): string {
  return item.kind === 'MAO_DE_OBRA'
    ? `mao:${item.jobRoleId ?? normalizeName(item.description)}`
    : `item:${item.code === undefined ? normalizeName(item.description) : normalizeName(item.code)}`;
}

/**
 * A média de cada serviço do apartamento × item da referência, ponderada (RN-518).
 *
 * Sem referência não há média (D3): tudo vazio, e quem chama diz por quê. O item de aço (com
 * bitola) fica fora — ele não tem índice, reparte pela taxa da disciplina (RN-378), e a média da
 * taxa é da etapa 3. A medida zero e a tipologia que não se repete (zero vezes) não entram.
 *
 * Nos serviços que medem o ambiente sai também a média por tipo de cômodo (RN-525): a quantidade
 * de cada cômodo é a medida dele × (o próprio digitado dele, ou o índice) × (1 + perda). O próprio
 * que veio da média fica fora (D5), e a quantidade digitada no total da tipologia também — ela não
 * se reparte por cômodo. O "Outro" não entra (A7).
 */
export function averageApartmentRates(
  input: ApartmentInput,
  reference: readonly ReferenceItem[] | undefined,
): ApartmentAverages {
  if (reference === undefined) return NO_APARTMENT_AVERAGES;

  const daReferencia = new Map<string, ReferenceItem[]>();
  reference.forEach((item) => {
    const chave = serviceLockKeyOf(item.disciplineName, item.serviceName);
    daReferencia.set(chave, [...(daReferencia.get(chave) ?? []), item]);
  });
  const ordemDaReferencia = new Map(reference.map((item, indice) => [item, indice]));

  const medidas = new Map<string, ApartmentMeasure[]>();
  for (const medida of input.measures) {
    if (!(medida.measured > 0) || !(medida.times > 0)) continue;
    const chave = `${medida.projectId}|${medida.serviceKey}`;
    medidas.set(chave, [...(medidas.get(chave) ?? []), medida]);
  }
  const digitadas = new Map(
    input.overrides.map((linha) => [`${linha.unitTypeId}|${linha.itemId}`, linha.quantity]),
  );
  const medidasDosComodos = new Map<string, ApartmentRoomMeasure[]>();
  for (const medida of input.roomMeasures ?? []) {
    if (medida.roomKind === 'OUTRO' || !(medida.measured > 0) || !(medida.times > 0)) continue;
    const chave = `${medida.projectId}|${medida.serviceKey}`;
    medidasDosComodos.set(chave, [...(medidasDosComodos.get(chave) ?? []), medida]);
  }
  const fatoresDosComodos = new Map(
    (input.roomFactors ?? []).map((linha) => [
      `${linha.roomId}|${linha.serviceKey}|${linha.itemKey}`,
      linha,
    ]),
  );

  const acumulados = new Map<string, Acumulado>();
  const porTipo = new Map<string, AcumuladoDoTipo>();
  const fora = new Map<string, ApartmentOutOfAverage>();

  for (const item of input.items) {
    if (!isApartmentService(item) || item.steelGauge !== undefined) continue;

    // D5: o índice que veio da média não é número de ninguém — a média não se realimenta.
    const doServico =
      item.indexOrigin === 'MEDIA'
        ? []
        : (medidas.get(`${item.projectId}|${item.serviceKey}`) ?? []);
    const comNumero = doServico.flatMap((medida) => {
      const quantidade = ownQuantity(
        item,
        medida.measured,
        digitadas.get(`${medida.unitTypeId}|${item.id}`),
      );
      return quantidade === undefined ? [] : [{ medida, quantidade }];
    });
    const nosComodos = descendsToRooms(item)
      ? numerosDosComodos(
          item,
          medidasDosComodos.get(`${item.projectId}|${item.serviceKey}`) ?? [],
          fatoresDosComodos,
        )
      : [];
    if (comNumero.length === 0 && nosComodos.length === 0) continue;

    const servico = serviceLockKey(item.serviceKey);
    const par = referenceItemFor(item, daReferencia.get(servico) ?? []);
    if (par === undefined) {
      const chave = `${item.projectId}|${servico}|${normalizeName(item.description)}`;
      const antes = fora.get(chave);
      fora.set(chave, {
        reason: 'ITEM_SEM_PAR',
        projectId: item.projectId,
        serviceName: item.serviceName,
        description: item.description,
        unitTypes: (antes?.unitTypes ?? 0) + Math.max(comNumero.length, 1),
      });
      continue;
    }

    const unidade = apartmentMeasureUnit({
      measureBy: item.measureBy,
      ...(item.appliesTo === undefined ? {} : { appliesTo: item.appliesTo }),
      ...(item.serviceUnit === undefined ? {} : { unit: item.serviceUnit }),
    });
    for (const { medida, quantidade } of nosComodos) {
      const chaveDoTipo = `${servico}|${apartmentReferenceKey(par)}|${medida.roomKind}`;
      const doTipo = porTipo.get(chaveDoTipo) ?? {
        serviceKey: servico,
        item: par,
        roomKind: medida.roomKind,
        unit: unidade,
        ordem: ordemDaReferencia.get(par) ?? 0,
        quantity: 0,
        measure: 0,
        projects: new Set<ProjectId>(),
        rooms: new Set<string>(),
      };
      doTipo.quantity += quantidade * medida.times;
      doTipo.measure += medida.measured * medida.times;
      doTipo.projects.add(medida.projectId);
      doTipo.rooms.add(medida.roomId);
      porTipo.set(chaveDoTipo, doTipo);
    }
    if (comNumero.length === 0) continue;

    const chave = `${servico}|${apartmentReferenceKey(par)}`;
    const acumulado = acumulados.get(chave) ?? {
      serviceKey: servico,
      item: par,
      unit: apartmentMeasureUnit({
        measureBy: item.measureBy,
        ...(item.appliesTo === undefined ? {} : { appliesTo: item.appliesTo }),
        ...(item.serviceUnit === undefined ? {} : { unit: item.serviceUnit }),
      }),
      ordem: ordemDaReferencia.get(par) ?? 0,
      quantity: 0,
      measure: 0,
      projects: new Set<ProjectId>(),
      unitTypes: new Set<string>(),
    };
    for (const { medida, quantidade } of comNumero) {
      acumulado.quantity += quantidade * medida.times;
      acumulado.measure += medida.measured * medida.times;
      acumulado.unitTypes.add(`${medida.projectId}|${medida.unitTypeId}`);
    }
    acumulado.projects.add(item.projectId);
    acumulados.set(chave, acumulado);
  }

  const averages = [...acumulados.values()]
    .sort((a, b) => a.ordem - b.ordem)
    .map((a): ApartmentAverage => ({
      serviceKey: a.serviceKey,
      disciplineName: a.item.disciplineName,
      serviceName: a.item.serviceName,
      item: a.item,
      measureUnit: a.unit,
      rate: a.quantity / a.measure,
      quantity: a.quantity,
      measure: a.measure,
      projects: a.projects.size,
      unitTypes: a.unitTypes.size,
    }));
  const byRoomKind = [...porTipo.values()]
    .sort((a, b) => a.ordem - b.ordem || a.roomKind.localeCompare(b.roomKind))
    .map((a): ApartmentRoomKindAverage => ({
      serviceKey: a.serviceKey,
      disciplineName: a.item.disciplineName,
      serviceName: a.item.serviceName,
      item: a.item,
      roomKind: a.roomKind,
      measureUnit: a.unit,
      rate: a.quantity / a.measure,
      quantity: a.quantity,
      measure: a.measure,
      projects: a.projects.size,
      rooms: a.rooms.size,
    }));
  return {
    averages,
    outOfAverage: [...fora.values()],
    byRoomKind,
    ...averageSlabSteelRates(input.steelRates ?? [], input.steelVolumes ?? []),
  };
}

/**
 * A média da taxa de aço da laje, bitola a bitola (RN-532):
 *
 * ```text
 * taxa média da bitola = Σ (taxa da obra × volume da laje dela) ÷ Σ volume
 * ```
 *
 * Entra a disciplina que tem volume de laje na foto e alguma taxa digitada. A bitola que ela não
 * usa entra com zero — a obra que arma a laje só com 8 e 10 mm diz que não usa 12,5 —, e assim a
 * soma das médias é a média da taxa total. A bitola que veio da média fica fora daquela obra, no
 * numerador e no volume (D5). A tipologia que não se repete (zero vezes) não entra.
 */
export function averageSlabSteelRates(
  rates: readonly ApartmentSteelRate[],
  volumes: readonly ApartmentSteelVolume[],
): Pick<ApartmentAverages, 'steel' | 'slabVolume'> {
  const volumePorDisciplina = new Map<string, number>();
  // A laje da tipologia é uma só, mesmo que duas disciplinas comprem aço dela.
  const lajePorTipologia = new Map<string, number>();
  for (const linha of volumes) {
    if (!(linha.volume > 0) || !(linha.times > 0)) continue;
    const volume = linha.volume * linha.times;
    const chave = `${linha.projectId}|${linha.disciplineKey}`;
    volumePorDisciplina.set(chave, (volumePorDisciplina.get(chave) ?? 0) + volume);
    const tipologia = `${linha.projectId}|${linha.unitTypeId}`;
    lajePorTipologia.set(tipologia, Math.max(lajePorTipologia.get(tipologia) ?? 0, volume));
  }
  const slabVolume = [...lajePorTipologia.values()].reduce((soma, volume) => soma + volume, 0);

  const taxasPorDisciplina = new Map<string, ApartmentSteelRate[]>();
  for (const taxa of rates) {
    const chave = `${taxa.projectId}|${taxa.disciplineKey}`;
    taxasPorDisciplina.set(chave, [...(taxasPorDisciplina.get(chave) ?? []), taxa]);
  }
  // A lista da laje, na disciplina que mistura elementos (Fase 42, RN-573).
  for (const [chave, taxas] of taxasPorDisciplina) {
    taxasPorDisciplina.set(chave, [...steelRowsFor(taxas, 'LAJE')]);
  }

  const acumulados = new Map<SteelGauge, { kg: number; volume: number; projects: Set<string> }>();
  for (const [chave, taxas] of taxasPorDisciplina) {
    const volume = volumePorDisciplina.get(chave) ?? 0;
    const digitadas = taxas.filter((taxa) => taxa.origin !== 'MEDIA' && taxa.kgPerM3 > 0);
    if (!(volume > 0) || digitadas.length === 0) continue;
    const daMedia = new Set(taxas.filter((taxa) => taxa.origin === 'MEDIA').map((t) => t.gauge));
    for (const { code } of STEEL_GAUGES) {
      if (daMedia.has(code)) continue;
      const taxa = digitadas.find((candidata) => candidata.gauge === code)?.kgPerM3 ?? 0;
      const acumulado = acumulados.get(code) ?? { kg: 0, volume: 0, projects: new Set<string>() };
      acumulado.kg += taxa * volume;
      acumulado.volume += volume;
      if (taxa > 0) acumulado.projects.add(taxas[0]!.projectId);
      acumulados.set(code, acumulado);
    }
  }

  const steel = STEEL_GAUGES.flatMap(({ code }): ApartmentSteelAverage[] => {
    const acumulado = acumulados.get(code);
    if (acumulado === undefined || !(acumulado.kg > 0)) return [];
    return [
      {
        gauge: code,
        rate: acumulado.kg / acumulado.volume,
        volume: acumulado.volume,
        projects: acumulado.projects.size,
      },
    ];
  });
  return { steel, slabVolume };
}

interface AcumuladoDoTipo {
  readonly serviceKey: string;
  readonly item: ReferenceItem;
  readonly roomKind: RoomKind;
  readonly unit: string;
  readonly ordem: number;
  quantity: number;
  measure: number;
  readonly projects: Set<ProjectId>;
  readonly rooms: Set<string>;
}

/**
 * O número próprio de um item em cada cômodo (RN-525): a medida do cômodo × (o próprio digitado
 * dele, ou o índice da obra) × (1 + perda). O próprio que veio da média, e o índice que veio da
 * média, não são número de ninguém (D5).
 */
function numerosDosComodos(
  item: ApartmentItem,
  medidas: readonly ApartmentRoomMeasure[],
  fatores: ReadonlyMap<string, ApartmentRoomFactor>,
): { readonly medida: ApartmentRoomMeasure; readonly quantidade: number }[] {
  const itemKey = normalizeName(item.description);
  return medidas.flatMap((medida) => {
    const proprio = fatores.get(`${medida.roomId}|${item.serviceKey}|${itemKey}`);
    if (proprio?.origin === 'MEDIA') return [];
    if (proprio === undefined && item.indexOrigin === 'MEDIA') return [];
    const fator = proprio?.factor ?? item.consumptionIndex;
    const quantidade = ownQuantity(
      {
        kind: item.kind,
        ...(fator === undefined ? {} : { consumptionIndex: fator }),
        ...(item.lossPercent === undefined ? {} : { lossPercent: item.lossPercent }),
      },
      medida.measured,
      undefined,
    );
    return quantidade === undefined ? [] : [{ medida, quantidade }];
  });
}
